/**
 * Minimal client-side logging utility
 * ONLY for critical errors that need to be reported to the server
 * All business logic, debugging, and non-critical logging should be removed
 * or handled server-side via API calls
 */

interface LogContext {
  [key: string]: any;
}

class ClientLogger {
  /**
   * Report critical error to server
   * Only use for errors that cannot be handled server-side
   * All other logging should be done server-side
   */
  async error(
    message: string,
    error?: Error | unknown,
    context?: LogContext
  ): Promise<void> {
    const errorObj =
      error instanceof Error
        ? error
        : error
          ? new Error(String(error))
          : new Error(message);

    const timestamp = new Date();

    // Always report critical errors to server (terminal and customer support logs via serverLogger)
    try {
      await fetch("/api/error-log", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          error: {
            message: errorObj.message,
            name: errorObj.name,
            stack: errorObj.stack,
          },
          errorType: "error",
          context: {
            ...context,
            timestamp: timestamp.toISOString(),
            timestampUnix: timestamp.getTime(),
          },
          timestamp: timestamp.toISOString(),
          userAgent: typeof navigator !== "undefined" && navigator ? navigator.userAgent : "",
          url: typeof window !== "undefined" && window && window.location ? window.location.href : "",
        }),
      });
    } catch {
      // Silently fail - don't throw to prevent infinite error loops
    }
  }

  /**
   * Report warning to server
   * Only use for warnings that need to be tracked server-side
   */
  async warn(
    message: string,
    context?: LogContext
  ): Promise<void> {
    const timestamp = new Date();

    // Report warnings to server (terminal and customer support logs via serverLogger)
    try {
      await fetch("/api/error-log", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          error: {
            message,
            name: "Warning",
          },
          errorType: "warning",
          context: {
            ...context,
            timestamp: timestamp.toISOString(),
            timestampUnix: timestamp.getTime(),
          },
          timestamp: timestamp.toISOString(),
          userAgent: typeof navigator !== "undefined" && navigator ? navigator.userAgent : "",
          url: typeof window !== "undefined" && window && window.location ? window.location.href : "",
        }),
      });
    } catch {
      // Silently fail - don't throw to prevent infinite error loops
    }
  }
}

// Export singleton instance
export const logger = new ClientLogger();

// Export for direct use
export default logger;
